import type { ReactNode } from 'react'
import { Plus, MoreHorizontal, Pencil, Share } from '@/components/icons'
import { cn } from '@/lib/utils'

interface SectionTitleProps {
  label: string
  count?: number
  icon?: ReactNode
  onAdd?: () => void
  addDisabled?: boolean
  onEdit?: () => void
  onMore?: () => void
  /** Secondary share action — renders alongside the primary action. */
  onShare?: () => void
  shareDisabled?: boolean
  /** Pin the header to the top of the scroll container while its section is in view (iOS-style). */
  sticky?: boolean
  className?: string
}

export function SectionTitle({
  label,
  count,
  icon,
  onAdd,
  addDisabled,
  onEdit,
  onMore,
  onShare,
  shareDisabled,
  sticky,
  className,
}: SectionTitleProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between pb-4',
        sticky && 'sticky top-0 z-10 bg-surface-brand-L0',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        {icon}
        <p className="text-xs font-semibold uppercase text-content-primary-subtle-mid tracking-[1.5px] leading-4">
          {label}
        </p>
        {typeof count === 'number' && count > 0 && (
          <p className="text-xs font-semibold uppercase text-content-brand-bold tracking-[1.5px] leading-4">
            {count}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1">
        {onShare && (
          <button
            type="button"
            className="inline-flex items-center justify-center size-8 rounded-full disabled:opacity-50 disabled:pointer-events-none"
            onClick={onShare}
            disabled={shareDisabled}
            aria-label="Share"
          >
            <Share className="w-5 h-5 text-content-brand-bold" />
          </button>
        )}
        {onAdd && (
          <button
            type="button"
            className="inline-flex items-center justify-center size-8 rounded-full disabled:opacity-50 disabled:pointer-events-none"
            onClick={onAdd}
            disabled={addDisabled}
          >
            <Plus className="w-5 h-5 text-content-brand-bold" />
          </button>
        )}
        {!onAdd && onEdit && (
          <button
            type="button"
            className="inline-flex items-center justify-center size-8 rounded-full"
            onClick={onEdit}
          >
            <Pencil className="w-4 h-4 text-content-brand-bold" />
          </button>
        )}
        {!onAdd && !onEdit && onMore && (
          <button
            type="button"
            className="inline-flex items-center justify-center size-8 rounded-full"
            onClick={onMore}
          >
            <MoreHorizontal className="w-5 h-5 text-content-brand-bold" />
          </button>
        )}
      </div>
    </div>
  )
}
